import { addFavoritesProduct, deleteFavoritesProduct } from '@context/useBitrixUser/useBitrix';
import { getLocalizationById } from '@context/useMyRouting/helpers';
import restoreServiceData from '@pages/AdditionalServices/containers/methods/basket/restoreServiceData';
import { SetUpServiceData } from '@pages/AdditionalServices/containers/methods/basket/types';
import { isProductEquals } from '@pages/Basket/useBasket';
import { formatPrice } from '@pages/OfferDetail/helpers';
import { FavoriteProduct } from '@services/AuthService/query/loadUserQuery';
import { OrderProduct } from '@services/requests/orderService/orderProductsLoader/interface';
import {
  ContainerParameters,
  CustomCargoParameters,
  TruckParameters,
} from '@services/requests/orderService/types';
import { ContainersData } from '@services/requests/searchLoaders/containersLoader/ContainersLoaderQuery';
import { TrucksData } from '@services/requests/searchLoaders/trucksLoader/TrucksLoaderQuery';
import { UnitData } from '@services/requests/searchLoaders/unitLoader/UnitLoaderQuery';
import { useTranslation } from 'react-i18next';

export const getActionClassName = (product: OrderProduct) => {
  if (product.product_type === 'route') {
    return '';
  }

  if (product.product_type === 'service') {
    const data = JSON.parse(product.data);

    const productType = Array.isArray(data) ? data[0].type : data.type;

    if (productType === 'customs-payments') {
      return 'order-item__service-actions --top';
    }
  }

  return 'order-item__service-actions';
};

/**
 * Вычисление описания груза для фуры
 * @param trucks
 * @param cargo
 * @param langCode
 */
export const calculateCargoForTruck = (
  trucks: TrucksData[],
  cargo: TruckParameters,
  langCode?: string
): string => {
  const truck = trucks.find((t) => t.id === `${cargo.truckId}`);
  const { default_name = '?', localized_names_data = [] } = truck || {};
  const foundLocalization = localized_names_data.find((l) => l.lang_id === langCode);
  const lng = getLocalizationById(langCode)?.countryCode;
  const name = foundLocalization?.message?.trim() || default_name;

  const { t } = useTranslation('Common', { lng: lng ?? langCode });

  return `${name} (${formatPrice(cargo.eachTruckWeight, 0)} ${t('Common:kg')}) × ${
    cargo.truckQuantity
  }`;
};

/**
 * Вычисление описания груза для контейнера
 * @param containers
 * @param cargo
 */
export const calculateCargoForContainer = (
  containers: ContainersData[],
  cargo: ContainerParameters,
  langCode?: string
): string => {
  const container = containers.find((c) => c.id === `${cargo.containerId}`);
  const { default_name = '?', localized_names_data = [] } = container || {};
  const foundLocalization = localized_names_data.find((l) => l.lang_id === langCode);
  const lng = getLocalizationById(langCode)?.countryCode;
  const name = foundLocalization?.message?.trim() || default_name;

  const { t } = useTranslation('Common', { lng: lng ?? langCode });

  return `${name} (${formatPrice(cargo.eachContainerWeight, 0)} ${t('Common:kg')}) × ${
    cargo.containerQuantity
  }`;
};

/**
 * Вычисление описания груза для фур
 * @param trucks
 * @param cargoData
 * @param langCode
 */
export const calculateCargoForTrucks = (
  trucks: TrucksData[],
  cargoData?: TruckParameters[] | null,
  langCode?: string
): string[] => {
  if (!cargoData) {
    return [];
  }

  return cargoData.map((cargo) => calculateCargoForTruck(trucks, cargo, langCode));
};

/**
 * Вычисление описания груза для контейнеров
 * @param containers
 * @param cargoData
 * @param langCode
 */
export const calculateCargoForContainers = (
  containers: ContainersData[],
  cargoData?: ContainerParameters[] | null,
  langCode?: string
): string[] => {
  if (!cargoData) {
    return [];
  }

  return cargoData.map((cargo) => calculateCargoForContainer(containers, cargo, langCode));
};

/**
 * Вычисление описания груза для одиночной сборки
 * @param units
 * @param cargo
 * @param langCode
 * @param langId
 */
export const calculateCargoForSingleCustom = (
  units: UnitData[],
  cargo: CustomCargoParameters,
  langCode?: string,
  langId?: string
): string => {
  const { t } = useTranslation('Common', { lng: langCode });
  switch (cargo.customCargoType) {
    case 'base':
      const { width, height, length, weight, quantity } = cargo.baseParameters;

      return `${t('Common:General')} (${t('Common:Width')}: ${width} ${t('common:m')} × ${t(
        'Common:Length'
      )}: ${length} ${t('common:m')} × ${t('Common:Height')}: ${height} ${t(
        'common:m'
      )} × ${weight} ${t('Common:kg')}) × ${quantity}`;
    case 'volume':
      const { volume, weight: party } = cargo.volumeParameters;

      return `${t('Common:Consignment')} (${volume} ${t('Common:m3')} × ${party} ${t(
        'Common:kg'
      )})`;
    case 'pallet':
      const {
        type,
        quantity: q,
        weight: pallet,
        normativeHeightOfStacking: n,
      } = cargo.palletParameters;

      const p = units.find((u) => u.id === type);

      const {
        length: l = '?',
        width: w = '?',
        default_name = '?',
        localized_names_data = [],
      } = p || {};

      const foundLocalization = localized_names_data.find((l) => l.lang_id === langId);
      const name = foundLocalization?.message?.trim() || default_name;

      return `${name} (${t('Common:Width')}: ${w} ${t('Common:m')} × ${t(
        'Common:Length'
      )}: ${l} ${t('Common:m')} × ${t('Common:Height')}: ${n} ${t('Common:m')} × ${pallet} ${t(
        'Common:kg'
      )}) × ${q}`;
  }

  return '';
};

/**
 * Вычисление описания груза для сборки
 * @param cargoData
 * @param units
 * @param langCode
 * @param langId
 */
export const calculateCargoForCustom = (
  units: UnitData[],
  cargoData?: CustomCargoParameters[] | null,
  langCode?: string,
  langId?: string
): string[] => {
  if (!cargoData) {
    return [];
  }

  return cargoData.map((cargo) => calculateCargoForSingleCustom(units, cargo, langCode, langId));
};

export const getCargo = (
  cargoType: 'container' | 'custom' | 'truck',
  entityLists: {
    containers: ContainersData[];
    units: UnitData[];
    trucks: TrucksData[];
  },
  cargoParams: {
    containers: ContainerParameters[];
    customs: CustomCargoParameters[];
    trucks: TruckParameters[];
  },
  langCode?: string,
  langId?: string
) => {
  switch (cargoType) {
    case 'custom':
      return calculateCargoForCustom(entityLists.units, cargoParams.customs, langCode, langId);
    case 'container':
      return calculateCargoForContainers(entityLists.containers, cargoParams.containers, langId);
    case 'truck':
      return calculateCargoForTrucks(entityLists.trucks, cargoParams.trucks, langId);
    default:
      return [];
  }
};

export const checkProductFavoritesExisting = (
  product: OrderProduct,
  favoritesProducts: FavoriteProduct[]
) => {
  const restoredFavoritesProducts = favoritesProducts
    .map((p) => restoreServiceData(JSON.parse(p.data)))
    .filter((d) => !!d);

  switch (product.product_type) {
    case 'service':
      const singleService = JSON.parse(product.data) as SetUpServiceData;
      return !!restoredFavoritesProducts.find((p) => {
        return p.setUpService.ServiceData.id === singleService.service.service_id.toString();
      });
    case 'route':
      return !!favoritesProducts.find((p) => {
        return isProductEquals(JSON.parse(p.data) as OrderProduct, product);
      });
    case 'complex':
      const complexService = JSON.parse(product.data).map((p) =>
        JSON.parse(p.data)
      )[0] as SetUpServiceData;
      if (!complexService?.IsSetupService) {
        return;
      }
      return !!restoredFavoritesProducts.find(
        (p) => p.setUpService.SelectedProductUUID === complexService.SelectedProductUUID
      );
  }
};

export const getFavoritesActionHandler = (
  product: OrderProduct,
  favoritesProducts: FavoriteProduct[]
) => {
  switch (product.product_type) {
    case 'route':
      const foundFavoriteProduct = favoritesProducts.find((p) =>
        isProductEquals(JSON.parse(p.data) as OrderProduct, product)
      );

      if (!foundFavoriteProduct) {
        addFavoritesProduct({ type: 'route', data: JSON.stringify(product) });
        return;
      }
      deleteFavoritesProduct({ id: foundFavoriteProduct.id });
      break;
    case 'complex':
      const complexService = JSON.parse(product.data).map((p) =>
        JSON.parse(p.data)
      )[0] as SetUpServiceData;
      if (!complexService?.IsSetupService) {
        return;
      }
      const foundFavoritesComplexProduct = favoritesProducts.find((f) =>
        f.data.includes(complexService.SelectedProductUUID)
      );

      if (!foundFavoritesComplexProduct) {
        addFavoritesProduct({ type: 'complexProduct', data: JSON.stringify(product) });
        return;
      }

      deleteFavoritesProduct({ id: foundFavoritesComplexProduct.id });
      break;
    case 'service':
      const singleService = JSON.parse(product.data) as SetUpServiceData;

      if (!singleService?.IsSetupService) {
        return;
      }

      const foundFavoritesSingleProduct = favoritesProducts.find((f) =>
        f.data.includes(singleService.SelectedProductUUID)
      );

      if (!foundFavoritesSingleProduct) {
        addFavoritesProduct({
          type: 'service',
          data: JSON.stringify(product),
        });
        return;
      }

      deleteFavoritesProduct({ id: foundFavoritesSingleProduct.id });
      break;
  }
};
